import { darfErledigen, gehoertMirImplizit, type TaskStatus } from '@netcon-work/shared'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BookOpen, Clock, ListTodo, Plus, Sun } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AufgabenVerweis } from '../components/AufgabeKontext'
import { Festhalten } from '../components/mywork/Festhalten'
import { Topbar } from '../components/Topbar'
import { AufDemSchirm } from '../components/meintag/AufDemSchirm'
import { KlopftAn } from '../components/meintag/KlopftAn'
import { useMeinTagLage } from '../components/meintag/lage'
import { TelefonVorlauf } from '../components/meintag/TelefonMarkieren'
import { Vorlauf } from '../components/meintag/Vorlauf'
import { MarkiertPasstNicht, ZumEinplanen } from '../components/meintag/Wochenleiste'
import { dauerStunden } from '../components/meintag/tagesplan'
import { weltdauern } from '../lib/dauer'
import { WasAlsNaechstes } from '../components/WasAlsNaechstes'
import {
  MeinTagListe,
  useMarkiertListe,
  usePulsDaten,
  usePulsHandlungen,
} from '../components/meintag/Puls'
import { tagesschluesselVon, useTagesplan } from '../components/meintag/useTagesplan'
import { amTisch, terminAufgaben, type BezugAufgabe } from '../components/mywork/bezuege'
import { FadenAnzeige, FadenBlatt } from '../components/mywork/Faden'
import { Feierabend, feierabendGemacht } from '../components/mywork/Feierabend'
import { Mitschrift } from '../components/mywork/Mitschrift'
import { NaechsterTag } from '../components/mywork/NaechsterTag'
import { NotizenZurAufgabe } from '../components/mywork/NotizenZurAufgabe'
import { OffeneEnden } from '../components/mywork/OffeneEnden'
import {
  VorbereitungBlatt,
  type VorzubereitenderTermin,
} from '../components/mywork/VorbereitungBlatt'
import {
  AmTisch,
  EndeZeile,
  FensterKarte,
  JetztLinie,
  PulsKarte,
  TerminKarte,
  VorbeiBereich,
} from '../components/mywork/StromTeile'
import {
  dauerText,
  kopfSatz,
  uhrAus,
  passtUrteil,
  stromBauen,
  type StromEintrag,
  type StromTermin,
} from '../components/mywork/strom'
import { cn } from '../lib/cn'
import { useGelesenSetzen } from '../lib/gelesen'
import { useTelefonLayout } from '../lib/medien'
import { useStarten, useStatusSetzen } from '../lib/mutationen'
import { zaehlen } from '../lib/nutzung'
import { bezuegeQuery, serienBezuegeQuery, tageslageQuery, useBezug } from '../lib/mywork'
import { pulsOrtMerken } from '../lib/puls'
import { personenQuery, sitzungQuery, workspacesQuery } from '../lib/queries'
import { useAufgabenAllerWelten } from '../lib/welt'
import { EntwurfHinweis } from '../components/claude/EntwurfHinweis'

/**
 * **My Work** ([Z-24](../../../../docs/phasen/zusatz-24-my-work.md),
 * [E-453](../../../../docs/ENTSCHEIDUNGEN.md#e-453)) – Aufgaben, Termine und Puls auf einer
 * Zeitachse. Eine **zusätzliche** Ansicht: Mein Tag mit seinen vier Ansichten bleibt, wie es ist.
 *
 * Diese Datei setzt nur zusammen. Gerechnet wird in `components/mywork/strom.ts` (Strom, Satz)
 * und im Planer `useTagesplan` (dieselbe Rechnung wie der Kalender-Modus); gezeichnet in
 * `components/mywork/StromTeile.tsx`. Der Puls, die ☀-Liste, „Klopft an" und „Auf dem Schirm"
 * sind die Bausteine aus Mein Tag.
 *
 * Oben das Feld „Festhalten" (Stufe 2): Notiz oder ☐ Aufgabe, gehört zum laufenden Termin, zur
 * Puls-Aufgabe oder zum Termin mit der genannten Person. Am Telefon ist es das Blatt hinter ＋.
 */

/** Die Farben der Welten – dieselbe Reihe wie im Circle, damit der Puls dort und hier gleich aussieht. */
const FARBEN = ['#2a78d6', '#1baf7a', '#7b5ac4', '#b8752a', '#c2456b', '#2f8f9d']

type Reiter = 'jetzt' | 'tag' | 'liste'

/** Jede Minute einmal – für den Strom reicht das; die Uhr des Pulses tickt selbst. */
function useMinutenTakt(): number {
  const [takt, setTakt] = useState(0)
  useEffect(() => {
    const t = window.setInterval(() => setTakt((n) => n + 1), 60_000)
    return () => window.clearInterval(t)
  }, [])
  return takt
}

export function MyWork() {
  const lage = useMeinTagLage()
  const starten = useStarten()
  const gelesenSetzen = useGelesenSetzen()
  const telefon = useTelefonLayout()
  const takt = useMinutenTakt()
  const { data: sitzung } = useQuery(sitzungQuery)
  const { data: workspaces = [] } = useQuery(workspacesQuery)
  const meineWelt = workspaces.find(gehoertMirImplizit)
  /** Alle Welten, die mir gehören (Meine ToDos, Privat …) – „Wo" beim Anlegen. */
  const eigeneWelten = useMemo(
    () =>
      workspaces
        .filter(gehoertMirImplizit)
        .map((w) => ({ id: w.id, label: w.label, icon: w.icon ?? null })),
    [workspaces],
  )
  const [festhaltenOffen, setFesthaltenOffen] = useState(false)
  const [feierabendOffen, setFeierabendOffen] = useState(false)
  const [vorbereitenFuer, setVorbereitenFuer] = useState<VorzubereitenderTermin | null>(null)
  const [reiter, setReiter] = useState<Reiter>('jetzt')
  /** „Was als Nächstes" – der Vorlauf ganz, mit Nehmen und Starten (wie in Mein Tag). */
  const [ziehenOffen, setZiehenOffen] = useState(false)

  useEffect(() => {
    zaehlen(`ansicht:mywork${telefon ? '/telefon' : ''}`)
  }, [telefon])
  /** Die Puls-Marke oben führt zurück hierher, solange man den Puls hier bedient. */
  useEffect(() => {
    pulsOrtMerken('/my-work')
  }, [])

  /* ── Der Tag ── */
  const jetztDatum = useMemo(() => new Date(), [takt])
  const heute = tagesschluesselVon(jetztDatum)
  const jetztH = jetztDatum.getHours() + jetztDatum.getMinutes() / 60
  const tp = useTagesplan(heute, takt)
  const arbeitVon = tp.fenster.length ? Math.min(...tp.fenster.map((f) => f.von)) : 0
  const arbeitBis = tp.fenster.length ? Math.max(...tp.fenster.map((f) => f.bis)) : 0
  const termine: StromTermin[] = useMemo(
    () =>
      (tp.kalender?.termine ?? []).map((t) => ({
        id: t.id,
        betreff: t.betreff,
        beginn: t.beginn,
        ende: t.ende,
        ganztaegig: t.ganztaegig,
        zeigtAls: t.zeigtAls ?? 'busy',
        abgesagt: t.abgesagt,
        ort: t.ort ?? null,
        webLink: t.webLink ?? null,
        teilnehmer: t.teilnehmer ?? [],
        teilnehmerAdressen: t.teilnehmerAdressen ?? [],
        serie: t.serie ?? null,
      })),
    [tp.kalender],
  )
  const strom = useMemo(
    () => stromBauen({ termine, plan: tp.plan, jetzt: jetztH, arbeitVon, arbeitBis }),
    [termine, tp.plan, jetztH, arbeitVon, arbeitBis],
  )

  /* ── Termin ↔ Aufgabe ↔ Mensch (Stufe 3) ── */
  const { data: feste = [] } = useQuery(bezuegeQuery(heute, heute))
  const bezug = useBezug(heute)
  const { data: personen = [] } = useQuery(personenQuery)
  const { aufgaben: bestand } = useAufgabenAllerWelten()
  const aufgabenKarte = useMemo(
    () => new Map<string, BezugAufgabe>(bestand.map((a) => [a.id, a])),
    [bestand],
  )
  const serien = useMemo(
    () => [...new Set(termine.flatMap((t) => (t.serie ? [t.serie] : [])))],
    [termine],
  )
  const serienDaten = useQueries({ queries: serien.map((s) => serienBezuegeQuery(s)) })
  const serienFeste = useMemo(
    () =>
      new Map(
        serien.map((s, i) => [
          s,
          (serienDaten[i]?.data ?? []).map((b) => ({ ...b, serieId: s, terminTitel: '' })),
        ]),
      ),
    // Feste Länge: je Serie ändert sich nur der Stand (sonst warnt React bei neuen Serien).
    [serien, serienDaten.map((q) => q.dataUpdatedAt).join('|')],
  )
  /** Was das Notizbuch zu den Terminen weiß (AP 24.18) – ohne Freigabe eine leere Antwort. */
  const terminSchluessel = useMemo(
    () =>
      termine
        .filter((t) => !t.abgesagt && !t.ganztaegig)
        .map((t) => ({
          schluessel: t.id,
          titel: t.betreff,
          beginn: t.beginn,
          serie: t.serie ?? null,
        })),
    [termine],
  )
  const { data: tageslage } = useQuery(tageslageQuery(heute, terminSchluessel))
  const blattZu = (terminId: string) => tageslage?.termine[terminId]?.blatt ?? null

  /** Offene Punkte im laufenden Termin abhaken – Erledigt, oder Zur Abnahme, wo jemand abnimmt. */
  const statusSetzen = useStatusSetzen()
  /** Was zuletzt abgehakt wurde – 10 s „rückgängig", wie beim Festhalten. */
  const [abgehakt, setAbgehakt] = useState<{
    id: string
    titel: string
    vorher: TaskStatus
    nach: TaskStatus
  } | null>(null)
  useEffect(() => {
    if (!abgehakt) return
    const t = window.setTimeout(() => setAbgehakt(null), 10_000)
    return () => window.clearTimeout(t)
  }, [abgehakt])
  const abhaken = {
    kann: (id: string) => aufgabenKarte.has(id),
    tun: (id: string) => {
      const a = aufgabenKarte.get(id)
      if (!a) return
      const abnahme = {
        abnahmeDurchId: a.abnahmeDurchId ?? null,
        assigneeId: a.workAssigneeId ?? a.assigneeId ?? null,
        status: a.status as TaskStatus,
      }
      const nach: TaskStatus = darfErledigen(abnahme, lage.ichId ?? null) ? 'erledigt' : 'review'
      statusSetzen.mutate({ id, status: nach })
      setAbgehakt({ id, titel: a.title, vorher: a.status as TaskStatus, nach })
    },
  }
  const abgehaktHinweis = abgehakt ? (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 rounded-xl bg-good-bg px-3.5 py-2 text-[13.5px] text-good-text"
    >
      <span className="min-w-0 flex-1 truncate">
        {abgehakt.nach === 'erledigt' ? '✓ Erledigt' : '✓ Zur Abnahme'}: {abgehakt.titel}
      </span>
      <button
        type="button"
        onClick={() => {
          statusSetzen.mutate({ id: abgehakt.id, status: abgehakt.vorher })
          setAbgehakt(null)
        }}
        className="min-h-9 cursor-pointer font-semibold text-accent-deep"
      >
        rückgängig
      </button>
    </div>
  ) : null

  const dazuFuer = (t: StromTermin) =>
    terminAufgaben({
      termin: t,
      feste,
      serienFeste: (t.serie && serienFeste.get(t.serie)) || [],
      aufgaben: aufgabenKarte,
      personen,
      ichId: lage.ichId ?? null,
    })
  const kennung = (t: StromTermin) => ({
    id: t.id,
    serie: t.serie ?? null,
    beginn: t.beginn,
    titel: t.betreff,
  })

  /* ── Puls ── */
  const farbeVon = useCallback(
    (weltId: string | null) => {
      const i = workspaces.findIndex((w) => w.id === weltId)
      return i < 0 ? '#8b8983' : (FARBEN[i % FARBEN.length] ?? '#2a78d6')
    },
    [workspaces],
  )
  const puls = usePulsDaten(workspaces, farbeVon)
  const roh = usePulsHandlungen(puls.aufgaben)

  /**
   * **Der Faden beim Wechsel** (Stufe 4): „Puls beenden" und „tauschen" fragen erst – ein Blatt,
   * zwei Zeilen, dann geht es weiter. „Später" hat sein eigenes Blatt (Wiedersehen) und fragt nicht.
   */
  const [fadenFrage, setFadenFrage] = useState<{
    taskId: string
    titel: string
    anlass: string
    weiter: () => void
    weiterText?: string
  } | null>(null)
  const aktivAufgabe = puls.stand ? puls.aufgaben.get(puls.stand.aktivId) : undefined
  const fragen = (anlass: string, weiter: () => void, weiterText?: string) => {
    if (!aktivAufgabe) return weiter()
    setFadenFrage({
      taskId: aktivAufgabe.id,
      titel: aktivAufgabe.title,
      anlass,
      weiter,
      ...(weiterText ? { weiterText } : {}),
    })
  }
  const h = {
    ...roh,
    beenden: () => fragen('Puls beenden', roh.beenden, 'Merken und beenden'),
    wechseln: () => fragen('Tauschen mit dem ruhenden', roh.wechseln, 'Merken und tauschen'),
  }
  const heuteListe = useMarkiertListe('heute', puls.aufgaben)
  const morgenListe = useMarkiertListe('morgen', puls.aufgaben)
  const imPuls = (id: string) => puls.stand?.aktivId === id || puls.stand?.ruhendId === id

  /** Was der Puls anbietet, wenn er frei ist: erst ☀ Heute, dann das Erste im laufenden Fenster. */
  const angebot = useMemo(() => {
    const sonne = heuteListe.find((x) => !imPuls(x.aufgabe.id))
    if (sonne)
      return { id: sonne.aufgabe.id, titel: sonne.aufgabe.title, grund: '☀ für heute vorgenommen' }
    const fenster = strom.eintraege.find(
      (e): e is Extract<StromEintrag, { art: 'fenster' }> => e.art === 'fenster' && e.jetztDrin,
    )
    const b = fenster?.bloecke.find((x) => x.aufgabe)
    return b?.aufgabe
      ? {
          id: b.aufgabe.id,
          titel: b.aufgabe.title,
          grund: b.art === 'vorschlag' ? 'Vorschlag für dieses Fenster' : 'liegt in diesem Fenster',
        }
      : null
  }, [heuteListe, strom, puls.stand?.aktivId, puls.stand?.ruhendId])

  /* ── Vorgenommen gegen frei ── */
  const geplant = useMemo(
    () =>
      tp.plan.bloecke
        .filter(
          (b) =>
            (b.art === 'markiert' || b.art === 'aufgabe' || b.art === 'fest') && b.bis > jetztH,
        )
        .reduce((n, b) => n + (b.bis - Math.max(b.von, jetztH)), 0),
    [tp.plan, jetztH],
  )
  const urteil = tp.plan.markiertPasstNicht.length
    ? 'zuviel'
    : passtUrteil(geplant, strom.freiStunden)
  const laufend = strom.eintraege.find(
    (e): e is Extract<StromEintrag, { art: 'jetzt' }> => e.art === 'jetzt',
  )?.laufend
  const laufendEnde = strom.eintraege.find(
    (e): e is Extract<StromEintrag, { art: 'termin' }> =>
      e.art === 'termin' && e.termin.id === laufend?.id,
  )?.bis
  /**
   * **Ein Termin beginnt, und der Puls läuft** – einmal je Termin, ab zwei Minuten vorher: Wo
   * stehst du? „Merken" pausiert den Puls; er ruht, bis man ihn nach dem Termin weiterlaufen lässt.
   */
  const gefragtFuer = useRef(new Set<string>())
  const baldOderGerade = useMemo(
    () =>
      strom.eintraege.find(
        (e): e is Extract<StromEintrag, { art: 'termin' }> =>
          e.art === 'termin' &&
          e.termin.zeigtAls !== 'free' &&
          e.von - jetztH <= 2 / 60 &&
          jetztH - e.von <= 10 / 60,
      ),
    [strom, jetztH],
  )
  useEffect(() => {
    const t = baldOderGerade
    if (!t || !puls.stand || puls.stand.pausiertSeit || fadenFrage) return
    if (gefragtFuer.current.has(t.termin.id)) return
    gefragtFuer.current.add(t.termin.id)
    fragen(
      t.von > jetztH
        ? `Gleich: ${t.termin.betreff} (${uhrAus(t.von)})`
        : `${t.termin.betreff} läuft seit ${uhrAus(t.von)}`,
      roh.pausieren,
      'Merken und Puls pausieren',
    )
    // `fragen` und `roh` ändern sich mit jedem Rendern – gefragt wird über die Kennung einmal.
  }, [baldOderGerade, puls.stand, fadenFrage])

  const satz = kopfSatz({
    vorname: sitzung?.displayName?.split(' ')[0] ?? null,
    stunde: jetztH,
    strom,
    laufend: laufend?.betreff ?? null,
    restMinuten: laufendEnde ? Math.max(0, Math.round((laufendEnde - jetztH) * 60)) : 0,
    vorgenommen: heuteListe.length,
    urteil,
    arbeitVorbei: arbeitBis > 0 && jetztH >= arbeitBis,
  })

  const datumText = jetztDatum.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })

  /* ── Teile ── */
  const kopf = (
    <div className="flex items-start gap-4">
      <div
        aria-hidden="true"
        className="bg-gradient-to-br from-accent to-pool-projekte bg-clip-text text-[40px] leading-none font-extrabold text-transparent sm:text-[44px]"
      >
        {jetztDatum.getDate()}
      </div>
      <div className="min-w-0">
        {strom.ganztaegig.length > 0 && (
          <div className="text-[13px] text-ink2">
            ganztägig: {strom.ganztaegig.map((t) => t.betreff).join(', ')}
          </div>
        )}
        <p className="mt-1 max-w-[820px] text-[15px] text-[#3d3b37]">
          {satz.map((t, i) => (t.fett ? <b key={i}>{t.text}</b> : <span key={i}>{t.text}</span>))}
        </p>
        {!tp.kalender?.verbunden && tp.kalender !== undefined && (
          <p className="mt-1 text-[12.5px] text-muted">
            Ohne Outlook-Verbindung – der Tag zeigt nur Arbeitszeit und Aufgaben.
          </p>
        )}
      </div>
    </div>
  )

  const vorbeiTermine = strom.eintraege.find((e) => e.art === 'vorbei')?.termine ?? []

  const stromListe = (nurNaechste = false) => {
    let eintraege = strom.eintraege
    if (nurNaechste) {
      const i = eintraege.findIndex((e) => e.art === 'jetzt')
      eintraege = eintraege.slice(i + 1, i + 4)
    }
    return (
      <div className="flex flex-col gap-2.5">
        {abgehaktHinweis}
        {eintraege.map((e, i) => {
          switch (e.art) {
            case 'vorbei':
              /** Steht mit den Notizen des Tages im Bereich vor dem Jetzt. */
              return null
            case 'jetzt':
              return (
                <div key="jetzt" className="flex flex-col gap-2.5">
                  <VorbeiBereich
                    termine={vorbeiTermine}
                    notizen={tageslage?.notizenOhneTermin ?? []}
                    karte={(t, von, bis) => (
                      <TerminKarte
                        eintrag={{ art: 'termin', termin: t, von, bis, laufend: false }}
                        jetzt={jetztH}
                        vorbei
                        dazu={dazuFuer(t)}
                        notizbuch={tageslage?.termine[t.id]}
                        loesen={(taskId) => bezug.loesen.mutate({ terminId: t.id, taskId })}
                      />
                    )}
                  />
                  <JetztLinie eintrag={e} />
                  <div className="sm:pl-[66px] pl-[58px]">{pulsKarte}</div>
                </div>
              )
            case 'termin':
              return (
                <TerminKarte
                  key={`t-${e.termin.id}-${i}`}
                  eintrag={e}
                  jetzt={jetztH}
                  dazu={dazuFuer(e.termin)}
                  notizbuch={tageslage?.termine[e.termin.id]}
                  festMachen={(taskId) =>
                    bezug.setzen.mutate({ termin: kennung(e.termin), taskId, quelle: 'hand' })
                  }
                  loesen={(taskId) => bezug.loesen.mutate({ terminId: e.termin.id, taskId })}
                  vorbereiten={() =>
                    setVorbereitenFuer({ titel: e.termin.betreff, beginn: e.termin.beginn })
                  }
                  abhaken={abhaken}
                  mitschrift={
                    e.laufend && tageslage?.termine[e.termin.id] ? (
                      <Mitschrift
                        termin={e.termin}
                        blatt={blattZu(e.termin.id)}
                        vorbereiten={
                          tageslage.termine[e.termin.id]?.reihe
                            ? () =>
                                setVorbereitenFuer({
                                  titel: e.termin.betreff,
                                  beginn: e.termin.beginn,
                                })
                            : undefined
                        }
                        feld={
                          <Festhalten
                            eingebettet
                            laufend={e.termin}
                            kommende={[]}
                            puls={null}
                            meineWelt={meineWelt ?? null}
                            ichId={lage.ichId ?? null}
                            blattZu={blattZu}
                            amTermin={(taskId, t) =>
                              bezug.setzen.mutate({
                                termin: kennung(t),
                                taskId,
                                quelle: 'festhalten',
                              })
                            }
                          />
                        }
                      />
                    ) : undefined
                  }
                />
              )
            case 'fenster':
              return (
                <FensterKarte
                  key={`f-${e.von}`}
                  eintrag={e}
                  nurFuer={nurFuer}
                  erledigen={(id) => (abhaken.kann(id) ? abhaken.tun(id) : undefined)}
                  jetzt={jetztH}
                  imPuls={imPuls}
                  holen={(id) => h.holen(id)}
                />
              )
            case 'ende':
              return (
                <EndeZeile key="ende" um={e.um} abschliessen={() => setFeierabendOffen(true)} />
              )
          }
        })}
      </div>
    )
  }

  /** Nach Feierabend ohne laufenden Puls und ohne Angebot ist die Karte nur Lärm. */
  const pulsKarte =
    !puls.stand && !angebot && arbeitBis > 0 && jetztH >= arbeitBis ? null : (
      <PulsKarte
        stand={puls.stand}
        aufgaben={puls.aufgaben}
        jetzt={puls.jetzt}
        summen={puls.summen}
        termine={puls.termine}
        h={h}
        angebot={angebot}
        unterBogen={
          aktivAufgabe ? (
            <>
              <FadenAnzeige
                taskId={aktivAufgabe.id}
                aendern={() =>
                  setFadenFrage({
                    taskId: aktivAufgabe.id,
                    titel: aktivAufgabe.title,
                    anlass: 'Faden ändern',
                    weiter: () => undefined,
                  })
                }
              />
              <NotizenZurAufgabe aufgabeId={aktivAufgabe.id} />
            </>
          ) : null
        }
      />
    )

  const vorgenommenKopf = (
    <div className="rounded-card border border-line bg-surface px-3.5 py-3 shadow-task">
      <div className="flex items-baseline justify-between gap-2 text-[12.5px]">
        <span>
          <b>{dauerText(geplant)}</b> geplant · <b>{dauerText(strom.freiStunden)}</b> frei
        </span>
        <span
          className={cn(
            'font-semibold',
            urteil === 'passt' && 'text-good-text',
            urteil === 'knapp' && 'text-sonne',
            urteil === 'zuviel' && 'text-bad',
            urteil === 'leer' && 'text-muted',
          )}
        >
          {urteil === 'passt'
            ? 'passt'
            : urteil === 'knapp'
              ? 'passt knapp'
              : urteil === 'zuviel'
                ? 'zu viel'
                : heuteListe.length > 0
                  ? 'nicht eingeplant'
                  : 'nichts vorgenommen'}
        </span>
      </div>
      <div
        className="mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-[#efeee9]"
        aria-hidden="true"
      >
        <div
          className={cn(
            urteil === 'zuviel' ? 'bg-bad' : urteil === 'knapp' ? 'bg-[#e3b55c]' : 'bg-good',
          )}
          style={{
            width: `${Math.min(100, strom.freiStunden ? (geplant / strom.freiStunden) * 100 : 0)}%`,
          }}
        />
      </div>
    </div>
  )

  /**
   * **Nur Zeit für eine Welt** – am Samstag steht oft nur das Fenster von PRIVAT (eigene
   * Arbeitszeit der Welt, AP 19.4). Aufgaben aus anderen Welten passen dann nicht hinein; ohne
   * diesen Satz sah das wie ein Fehler aus (Nils, 24.10.2026, am Telefon).
   */
  const nurFuer = useMemo(() => {
    if (tp.fenster.length === 0 || tp.fenster.some((f) => !f.weltId)) return null
    const namen = [
      ...new Set(
        tp.fenster.map((f) => workspaces.find((w) => w.id === f.weltId)?.label ?? 'eine Welt'),
      ),
    ]
    return namen.join(' und ')
  }, [tp.fenster, workspaces])

  /** Gibt es heute nur die Zeit einer eigenen Welt, schlägt Festhalten sie als „Wo" vor. */
  const weltDesTages =
    tp.fenster.length > 0 && tp.fenster.every((f) => f.weltId)
      ? (eigeneWelten.find((w) => tp.fenster.some((f) => f.weltId === w.id))?.id ?? null)
      : null

  const dauern = useMemo(() => weltdauern(tp.welten), [tp.welten])
  const passtNicht = (
    <MarkiertPasstNicht
      telefon={telefon}
      ziehbar={false}
      aufgaben={tp.plan.markiertPasstNicht}
      dauer={(id) => {
        const a = tp.plan.markiertPasstNicht.find((x) => x.id === id)
        return a ? dauerStunden(a, dauern) : 0
      }}
      hinweis={
        nurFuer
          ? `Heute gibt es nur Zeit für ${nurFuer} – andere Welten passen nicht hinein.`
          : null
      }
    />
  )

  /** Was nicht markiert ist, in der Reihenfolge des Vorlaufs – wie im Kalender von Mein Tag. */
  const zumEinplanen = useMemo(
    () => lage.naechstes.filter((z) => !tp.markierungen.has(z.id)),
    [lage.naechstes, tp.markierungen],
  )
  const einplanen = (
    <>
      <ZumEinplanen zeilen={zumEinplanen} telefon={telefon} ziehbar={false} />
      {tp.plan.rest.length > 0 && (
        <section className="rounded-card border border-line bg-surface shadow-task">
          <h2 className="flex items-center justify-between border-b border-line px-4 py-3 text-[13.5px]">
            Passt heute nicht mehr
            <span className="text-[12px] font-normal text-muted">
              {tp.plan.rest.length} Stück · angefangen
            </span>
          </h2>
          {nurFuer && (
            <p className="border-b border-line px-4 py-2 text-[12px] text-ink2">
              Heute gibt es nur Zeit für {nurFuer}.
            </p>
          )}
          <ul>
            {tp.plan.rest.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-2 border-b border-line px-4 py-2 last:border-0"
              >
                <AufgabenVerweis
                  aufgabeId={a.id}
                  className="min-w-0 flex-1 truncate text-left text-[13px] font-medium hover:underline"
                >
                  {a.title}
                </AufgabenVerweis>
                {!imPuls(a.id) && (
                  <button
                    type="button"
                    onClick={() => h.holen(a.id)}
                    aria-label={`In den Puls: ${a.title}`}
                    className="h-[28px] shrink-0 cursor-pointer rounded-lg border border-[#bcd5f3] bg-accent-soft px-2 text-[12.5px] font-semibold text-accent-deep"
                  >
                    ▶
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )

  const vorgenommen = (
    <section aria-label="Vorgenommen" className="flex flex-col gap-0">
      <h2 className="mb-2 text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
        ☀ Vorgenommen
      </h2>
      {vorgenommenKopf}
      <MeinTagListe
        aufgaben={puls.aufgaben}
        erledigtHeute={puls.erledigtHeute}
        summen={puls.summen}
        stand={puls.stand}
        h={h}
      />
    </section>
  )

  const amTischKarte = laufend ? (
    <AmTisch
      termin={laufend.betreff}
      zeilen={amTisch({
        termin: laufend,
        aufgaben: aufgabenKarte,
        personen,
        ichId: lage.ichId ?? null,
      })}
    />
  ) : null

  const offeneEnden = <OffeneEnden lage={tageslage} />

  /**
   * **Der Vorlauf** (Nils, 24.10.2026: *„Wie kann ich in My Work meinen Vorlauf oder die schnell
   * angelegten Aufgaben sehen? Und dann auch ziehen."*) – derselbe Baustein wie in Mein Tag, das
   * Nächste oben (hier steht er nicht über „Jetzt", sondern in der Seitenspalte). Nehmen und
   * Starten an der Zeile, „Alle ansehen" öffnet „Was als Nächstes" mit dem Vorrat zum Ziehen.
   */
  const vorlaufListe = (
    <Vorlauf
      rangfolge={lage.naechstes}
      wip={lage.wip}
      wipLimit={lage.wipLimit}
      fokusFuer={lage.fokusFuer}
      wipHinweisText={lage.wipHinweisText}
      hinweis={lage.zuvielHinweis}
      fensterOeffnen={() => setZiehenOffen(true)}
      naechstesOben
    />
  )
  const ziehenFenster = ziehenOffen ? (
    <WasAlsNaechstes
      rangfolge={lage.naechstes}
      pools={lage.pools}
      ichId={lage.ichId}
      wip={lage.wip}
      wipLimit={lage.wipLimit}
      hinweis={lage.zuvielHinweis}
      schliessen={() => setZiehenOffen(false)}
    />
  ) : null

  const unfertig = [
    ...heuteListe.map((x) => x.aufgabe),
    ...lage.jetzt.filter((a) => !heuteListe.some((x) => x.aufgabe.id === a.id)),
  ].map((a) => ({
    id: a.id,
    title: a.title,
    status: a.status,
    abnahmeDurchId: a.abnahmeDurchId ?? null,
    assigneeId: a.assigneeId ?? null,
  }))
  const feierabend = feierabendOffen ? (
    <Feierabend
      unfertig={unfertig}
      ichId={lage.ichId ?? null}
      erledigtHeute={puls.erledigtHeute}
      imPulsHeute={puls.summen.size}
      lage={tageslage}
      pulsLaeuft={!!puls.stand}
      pulsBeenden={roh.beenden}
      schliessen={() => setFeierabendOffen(false)}
      vorbereiten={setVorbereitenFuer}
    />
  ) : null
  const vorbereitungBlatt = vorbereitenFuer ? (
    <VorbereitungBlatt termin={vorbereitenFuer} schliessen={() => setVorbereitenFuer(null)} />
  ) : null
  const abschliessenKnopf = (
    <button
      type="button"
      onClick={() => setFeierabendOffen(true)}
      className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-line2 bg-surface px-3 py-1.5 text-[13px] font-semibold"
    >
      {feierabendGemacht() ? 'Feierabend ✓' : 'Tag abschließen'}
    </button>
  )

  /** Kommt heute noch etwas (Termin, Fenster)? Sonst zeigt My Work den nächsten Arbeitstag. */
  const heuteNochEtwas = strom.eintraege.some((e) => e.art === 'termin' || e.art === 'fenster')
  const naechsterTag = heuteNochEtwas ? null : (
    <NaechsterTag
      vorgenommen={morgenListe.map((x) => ({ id: x.aufgabe.id, title: x.aufgabe.title }))}
      abschliessen={() => setFeierabendOffen(true)}
      schonAbgeschlossen={feierabendGemacht()}
      vorbereiten={setVorbereitenFuer}
    />
  )

  const klopft = (
    <>
      <EntwurfHinweis />
      <KlopftAn
        meldungen={lage.klopft}
        ziehen={(id) => starten.mutate({ id })}
        gelesenSetzen={(v) => gelesenSetzen.mutate(v)}
      />
    </>
  )

  const schirm = (
    <AufDemSchirm
      angepinnt={lage.angepinnt}
      warte={lage.warte}
      markierungen={lage.markierungen}
      schlafend={lage.schlafend}
    />
  )

  const kommende = strom.eintraege.flatMap((e) =>
    e.art === 'termin' && !e.laufend ? [e.termin] : [],
  )
  const pulsAufgabe = puls.stand ? puls.aufgaben.get(puls.stand.aktivId) : undefined
  const festhalten = (blatt: boolean) => (
    <Festhalten
      laufend={laufend ?? null}
      kommende={kommende}
      puls={pulsAufgabe ? { id: pulsAufgabe.id, titel: pulsAufgabe.title } : null}
      meineWelt={meineWelt ?? null}
      ichId={lage.ichId ?? null}
      blatt={blatt}
      autoFocus={blatt}
      blattZu={blattZu}
      welten={eigeneWelten}
      weltVorschlag={weltDesTages}
      amTermin={(taskId, t) =>
        bezug.setzen.mutate({ termin: kennung(t), taskId, quelle: 'festhalten' })
      }
    />
  )

  /** Am Telefon: Festhalten als Blatt von unten. */
  const festhaltenBlatt = festhaltenOffen ? (
    <div
      className="unter-leiste fixed inset-x-0 bottom-0 z-[60] flex items-end bg-[rgb(23_22_20/0.35)]"
      role="dialog"
      aria-modal="true"
      aria-label="Festhalten"
      onClick={() => setFesthaltenOffen(false)}
    >
      <div
        className="w-full rounded-t-[22px] bg-surface px-4 pt-2.5 pb-[calc(20px+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgb(0_0_0/0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div aria-hidden="true" className="mx-auto mb-2 h-[5px] w-10 rounded-full bg-line2" />
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-[18px] font-bold">Festhalten</h2>
          <button
            type="button"
            onClick={() => setFesthaltenOffen(false)}
            className="min-h-11 cursor-pointer px-2 text-[14px] font-semibold text-accent-deep"
          >
            Fertig
          </button>
        </div>
        {festhalten(true)}
      </div>
    </div>
  ) : null

  const fadenBlatt = fadenFrage ? (
    <FadenBlatt
      taskId={fadenFrage.taskId}
      titel={fadenFrage.titel}
      anlass={fadenFrage.anlass}
      {...(fadenFrage.weiterText ? { weiterText: fadenFrage.weiterText } : {})}
      weiter={() => {
        const w = fadenFrage.weiter
        setFadenFrage(null)
        w()
      }}
      abbrechen={() => setFadenFrage(null)}
    />
  ) : null

  /* ── Telefon ── */
  if (telefon) {
    return (
      <>
        <Topbar titel="My Work" untertitel={datumText} ohnePuls />
        <div className="flex flex-col gap-4 pb-[120px]">
          {reiter === 'jetzt' && (
            <>
              {kopf}
              {pulsKarte}
              {amTischKarte}
              {lage.klopft.some((m) => !m.gelesen) && (
                <button
                  type="button"
                  onClick={() => setReiter('liste')}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-xl border border-[#f3d2d2] bg-surface px-3.5 text-left text-[14px]"
                >
                  <b className="text-bad">
                    {lage.klopft.filter((m) => !m.gelesen).length} klopfen an
                  </b>
                  <span className="text-ink2">– nicht bis morgen</span>
                  <span className="ml-auto font-semibold text-accent-deep">ansehen ›</span>
                </button>
              )}
              {naechsterTag ?? (
                <>
                  {strom.eintraege.some(
                    (x, j) =>
                      j > strom.eintraege.findIndex((y) => y.art === 'jetzt') && x.art !== 'ende',
                  ) && (
                    <h2 className="text-[10.5px] font-bold tracking-[0.08em] text-ink2 uppercase">
                      Als Nächstes
                    </h2>
                  )}
                  {stromListe(true)}
                  {jetztH >= arbeitBis - 0.5 && arbeitBis > 0 && abschliessenKnopf}
                </>
              )}
            </>
          )}
          {reiter === 'tag' && stromListe()}
          {reiter === 'liste' && (
            <>
              {klopft}
              {vorgenommen}
              {passtNicht}
              {einplanen}
              <TelefonVorlauf rangfolge={lage.naechstes}>{vorlaufListe}</TelefonVorlauf>
              {offeneEnden}
              {schirm}
            </>
          )}
        </div>
        {festhaltenBlatt}
        {fadenBlatt}
        {feierabend}
        {vorbereitungBlatt}
        {ziehenFenster}
        <nav
          aria-label="My Work – Reiter"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
        >
          <div className="grid grid-cols-5 items-end">
            <ReiterKnopf aktiv={reiter === 'jetzt'} text="Jetzt" onClick={() => setReiter('jetzt')}>
              <Clock className="size-[22px]" />
            </ReiterKnopf>
            <ReiterKnopf aktiv={reiter === 'tag'} text="Tag" onClick={() => setReiter('tag')}>
              <ListTodo className="size-[22px]" />
            </ReiterKnopf>
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setFesthaltenOffen(true)}
                aria-label="Festhalten"
                className="-mt-6 mb-2 flex size-[58px] cursor-pointer items-center justify-center rounded-full bg-accent text-white shadow-[0_6px_16px_rgb(42_120_214/0.35)] disabled:opacity-40"
              >
                <Plus className="size-[26px]" />
              </button>
            </div>
            <ReiterKnopf
              aktiv={reiter === 'liste'}
              text="Vorgenommen"
              onClick={() => setReiter('liste')}
            >
              <Sun className="size-[22px]" />
            </ReiterKnopf>
            <Link
              to={'/notizbuch' as string}
              className="flex min-h-14 flex-col items-center justify-center gap-[3px] text-[10.5px] font-semibold text-ink2"
            >
              <BookOpen className="size-[22px]" />
              Notizbuch
            </Link>
          </div>
        </nav>
      </>
    )
  }

  /* ── Rechner ── */
  return (
    <>
      <Topbar titel="My Work" untertitel={datumText} ohnePuls aktion={abschliessenKnopf} />
      <div className="flex flex-col gap-5 pb-10">
        {kopf}
        {festhalten(false)}
        <div className="flex flex-wrap items-start gap-6">
          <section aria-label="Dein Tag" className="min-w-0 flex-[999_1_520px]">
            <h2 className="mb-2.5 text-[16px] font-semibold">
              Dein Tag{' '}
              <span className="text-[13px] font-normal text-ink2">
                · {strom.fenster} {strom.fenster === 1 ? 'Fenster' : 'Fenster'} ·{' '}
                {dauerText(strom.freiStunden)} frei
              </span>
            </h2>
            {stromListe()}
            {naechsterTag && <div className="mt-3">{naechsterTag}</div>}
          </section>
          <aside
            aria-label="Seitenspalte"
            className="flex w-full min-w-0 flex-col gap-4 xl:w-[480px] xl:flex-none"
          >
            {amTischKarte}
            {klopft}
            {vorgenommen}
            {passtNicht}
            <div className="rounded-card border border-line bg-surface px-3.5 py-3 shadow-task">
              {vorlaufListe}
            </div>
            {einplanen}
            {offeneEnden}
            {schirm}
          </aside>
        </div>
      </div>
      {fadenBlatt}
      {feierabend}
      {vorbereitungBlatt}
      {ziehenFenster}
    </>
  )
}

function ReiterKnopf({
  aktiv,
  text,
  onClick,
  children,
}: {
  aktiv: boolean
  text: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={aktiv}
      onClick={onClick}
      className={cn(
        'flex min-h-14 cursor-pointer flex-col items-center justify-center gap-[3px] text-[10.5px] font-semibold',
        aktiv ? 'text-accent-deep' : 'text-ink2',
      )}
    >
      {children}
      {text}
    </button>
  )
}
